export enum ThreatHuntingRunStatus {
  STARTING = 'starting',
  READY = 'ready',
  COMPLETED = 'completed',
  TERMINATED = 'terminated',
  ABANDONED = 'abandoned',
}

export interface VmData {
  url: string;
  instance: {
    _id: string;
    startedAt: string;
    endsAt: string;
  };
}

export interface ThreatHuntingRun {
  scenario: string;
  runId: string;
  status: ThreatHuntingRunStatus;
  success: boolean;
  startedAt: string;
  endedAt: string | null;
  actualRuntime: number;
  resumedAt: string | null;
  isErrored?: boolean;
  vms:
    | {
        analyst: VmData;
        siem: VmData;
      }
    | undefined;
}

export enum ThreatHuntingVMs {
  ANALYST = 'analyst',
  SIEM = 'siem',
}

export enum ThreatHuntingHypothesisOutcome {
  PROVEN = 'proven',
  DISPROVEN = 'disproven',
}

export interface ThreatHuntingActiveRun extends ThreatHuntingRun {
  hasInvestigationStarted: boolean;
  users: string[];
  assets: string[];
  hypothesis: string;
  report: {
    hypothesisOutcomeClassification: ThreatHuntingHypothesisOutcome;
    threatReportEvaluation: string;
  };
  status: Extract<ThreatHuntingRunStatus, ThreatHuntingRunStatus.STARTING | ThreatHuntingRunStatus.READY>;
  vms: undefined | Record<`${ThreatHuntingVMs}`, VmData>;
  cmsTimelineStagesCount: number;
  notes: string;
}

export interface ThreatHuntingTactic {
  value: string;
  label: string;
}

export interface ThreatHuntingTechnique {
  value: string;
  label: string;
}

export interface MitreAccuracy {
  tactics: {
    tactic: string;
    id: string;
    techniques: {
      id: string;
      name: string;
      max: number;
      discovered: number;
    }[];
  }[];
}

export type ThreatHuntingSummaryLocationState = { new?: boolean };

export const THREAT_HUNTING_ONBOARDING_GUIDE_SLUG = 'threat-hunting-onboarding';

export interface ThreatHuntingRunReportAttackStage {
  id: string;
  title: string;
  description: string; // Adversary step description
  timestamp: string;
  tactic: string;
  tacticName: string;
  technique: string;
  techniqueName: string;
  user?: string; // Selected user
  asset: string; // Selected asset
  ioc: string;
  // siemUrlLink: string;
  evaluation?: string;
  adversaryStepDescriptionPointsAwarded: number;
  timestampPointsAwarded: number;
  tacticPointsAwarded: number;
  techniquePointsAwarded: number;
  userPointsAwarded: number;
  assetPointsAwarded: number;
  iocPointsAwarded: number;
  order: number;
}

export interface ThreatHuntingRunReport {
  scenario: string;
  startedAt: string;
  endedAt: string;
  title: string;
  success: boolean;
  pointsAwarded: number;
  overallRunEvaluation: string;
  hypothesis: string;
  report: {
    hypothesisOutcomeClassification: ThreatHuntingHypothesisOutcome;
    hypothesisOutcomePointsAwarded: number;
    threatReportEvaluation: string;
    techniquesAndTactics: {
      percentageIdentified: number;
      tactic: {
        name: string;
        tacticName: string;
        identified: boolean;
        stage: string;
      }[];
      technique: {
        name: string;
        techniqueName: string;
        identified: boolean;
        stage: string;
      }[];
    };
    compromisedAssets: {
      percentageIdentified: number;
      assets: {
        name: string;
        identified: boolean;
      }[];
    };
    iocs: {
      percentageIdentified: number;
      network: {
        name: string;
        identified: boolean;
        stage: string;
      }[];
      host: {
        name: string;
        identified: boolean;
        stage: string;
      }[];
    };
  };
  timelineStages: ThreatHuntingRunReportAttackStage[];
  actualRuntime: number;
  resumedAt?: string;
  user?: {
    username: string;
    avatar: string;
  };
  isAdmin?: boolean;
}

export type ThreatHuntingStatsRate = {
  yearAndMonth: string;
  rate: number;
}[];

export enum RateTypeMap {
  TECHNIQUE = 'technique',
  TACTIC = 'tactic',
}

export type RateType = (typeof RateTypeMap)[keyof typeof RateTypeMap];

export enum TrendTypeMap {
  Upward = 'upward',
  Downward = 'downward',
}

export type Trend = (typeof TrendTypeMap)[keyof typeof TrendTypeMap];

export interface Metric {
  total: number;
  monthly: number;
  percentageChange: number;
}

export interface MetricWithTrend {
  total: number;
  tillLastMonth?: number;
  monthly?: number;
  percentageChange: number;
  trend: Trend;
}

export interface ThreatHuntingStats {
  attempts: Metric;
  successCount: Metric;
  successRate: MetricWithTrend;
  averageTime: MetricWithTrend & { monthly: number };
  pointsCollected: MetricWithTrend & { monthly: number };
  averagePercentageIdentified: {
    techniquesAndTactics: number;
    compromisedAssets: number;
    iocs: number;
  };
  user: {
    username: string;
    avatar: string;
  };
}

export interface SharedLinksData {
  sharedSummaryLink: string;
  sharedImageLink: string;
}

export const SiemTool = {
  SPLUNK: 'splunk',
  ELASTIC: 'elastic',
  SENTINEL: 'sentinel',
} as const;

export type SiemTool = (typeof SiemTool)[keyof typeof SiemTool];
